import { css } from '@emotion/react';
import {
  Box,
  desktopFirstMediaQuery,
  Global,
  NotificationsProvider,
  Skeleton
} from '@inflearn/ds-react';
import { navigate, type HeadFC, type PageProps } from 'gatsby';
import SessionContent from '../../components/infcon-2023/sessionDetail/SessionContent';
import SessionInfo from '../../components/infcon-2023/sessionDetail/SessionInfo';
import SessionPageController from '../../components/infcon-2023/sessionDetail/SessionPageController';
import SessionTags from '../../components/infcon-2023/sessionDetail/SessionTags';
import SessionTitle from '../../components/infcon-2023/sessionDetail/SessionTitle';
import SpeakerSessionDto from '../../dtos/SpeakerSessionDto';
import useSpeakerSessionDetail from '../../hooks/useSpeakerSessionDetail';
import { GATSBY_SITE_URL } from '../../utils/env';
import Seo from '../../utils/Seo';
import { URLS } from '../../utils/urls';

export const Head: HeadFC = () => {
  return (
    <Seo
      title="인프콘 2023 발표/프로그램 - INFCON 2023"
      description="인프콘 발표 세션과 프로그램을 확인해보세요 - 인프런이 만드는 IT인의 축제, 인프콘으로 초대합니다. 인프콘과 함께 배우고 나누고 성장하세요. 다양한 발표 세션, 핸즈온, 네트워킹까지 만나보세요."
      fullUrl={`${GATSBY_SITE_URL}${URLS.INFCON_2023__SESSION}`}
    />
  );
};

const SessionDetail = ({ location }: PageProps) => {
  const params = new URLSearchParams(location.search);
  const id = params.get('id');

  const { data: response, isError, isLoading } = useSpeakerSessionDetail(Number(id));

  if (isError) {
    void navigate('/404');
  }

  if (isLoading || response === undefined) {
    return <SessionDetailSkeleton />;
  }

  const speakerSession = new SpeakerSessionDto(response);

  return (
    <>
      <NotificationsProvider />
      <Global styles={{ '.footer_bottom': { display: 'none !important' } }} />
      <Box component="article" css={{ minHeight: 'calc(100vh - 104px)' }}>
        <Box css={styleSessionDetailWrapper}>
          <SessionPageController
            sessionId={speakerSession.id}
            hasSchedule={speakerSession.hasSchedule}
            subType={speakerSession.subType}
          />
          <SessionInfo location={speakerSession.location} date={speakerSession.schedule} />
          <SessionTitle>{speakerSession.title}</SessionTitle>
          <SessionTags tags={speakerSession.tags} />
          <SessionContent
            profileImage={speakerSession.url}
            speakerName={speakerSession.speaker}
            speakerGroup={speakerSession.affiliation}
            body={speakerSession.body}
          />
        </Box>
      </Box>
    </>
  );
};

const SessionDetailSkeleton = () => {
  return (
    <Box css={[styleSessionDetailWrapper]}>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '24px',
          [desktopFirstMediaQuery.mobile]: {
            marginBottom: '16px'
          }
        }}>
        <Skeleton
          width={44}
          height={44}
          css={{ [desktopFirstMediaQuery.mobile]: { height: '34px' } }}
        />
        <Skeleton
          width={80}
          height={44}
          css={{ [desktopFirstMediaQuery.mobile]: { height: '34px' } }}
        />
      </Box>
      <Box mb={12} css={{ display: 'flex', marginBottom: '12px' }}>
        <Skeleton
          width={60}
          height={24}
          mr={16}
          css={{
            [desktopFirstMediaQuery.mobile]: {
              height: '21px'
            }
          }}
        />
        <Skeleton
          width={120}
          height={24}
          css={{
            [desktopFirstMediaQuery.mobile]: {
              height: '21px'
            }
          }}
        />
      </Box>
      <Skeleton
        width="100%"
        height={45}
        mb={12}
        css={{
          [desktopFirstMediaQuery.mobile]: {
            height: '32px'
          }
        }}
      />
      <Box
        css={{
          display: 'flex',
          gap: '8px',
          marginBottom: '32px',
          [desktopFirstMediaQuery.mobile]: {
            marginBottom: '24px'
          }
        }}>
        <Skeleton width={100} height={26} />
        <Skeleton width={59} height={26} />
      </Box>
      <Box css={{ display: 'flex' }} mb={16}>
        <Skeleton width={54} height={54} radius={100} mr={8} />
        <Box>
          <Skeleton width={40} height={30} mb={4} />
          <Skeleton width={40} height={21} />
        </Box>
      </Box>
      <Skeleton width="100%" height={100} mb={40} />
      <Skeleton width="100%" height={100} />
    </Box>
  );
};

const styleSessionDetailWrapper = css({
  margin: '0 auto',
  padding: '40px 32px 80px',
  maxWidth: '744px',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '20px 16px 60px'
  }
});

export default SessionDetail;
